import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { User } from '@api/models/user.model';
import { UserService } from '@api/services/user.service';
import { InputComponent } from '@ui/input/input.component';
import { ModalComponent } from '@ui/modal/modal.component';
import { MODAL_REF } from '@ui/modal/modal.service';
import { ModalService } from '@ui/modal/modal.service';
import { ModalRef } from '@ui/modal/modal.service';
import { NotificationService } from '@ui/notification/notification.service';
import { ButtonComponent } from '@ui/button/button.component';
import { ConfirmService } from '@ui/confirm-modal/confirm.service';
@Component({
  selector: 'app-users-modal',
  standalone: true,
  imports: [CommonModule, ModalComponent, InputComponent, ReactiveFormsModule, ButtonComponent],
  template: ` <ui-modal
    [title]="user ? 'Gebruiker aanpassen' : 'Gebruiker uitnodigen'"
    [submitText]="'Opslaan'"
    [cancelText]="'Annuleren'"
    (close)="closeModal()"
    (save)="saveModal()"
  >
    <form [formGroup]="form">
      <div class="flex flex-col gap-4">
        <ui-input
          id="name"
          label="Naam"
          placeholder="Naam"
          formControlName="name"
          [required]="true"
        />
        <ui-input
          id="email"
          label="Email"
          placeholder="Email"
          formControlName="email"
          [required]="true"
          [autocomplete]="'off'"
        />
        <ui-input
          type="checkbox"
          id="is_admin"
          label="Is admin"
          formControlName="is_admin"
        />

        @if(this.user?.verified_at) {
          <p class="text-sm text-gray-500">Email geverifieerd op {{ this.user?.verified_at | date:'dd-MM-yyyy HH:mm' }}</p>
        } @else if(this.user?.id) {
          <ui-button
            [variant]="'secondary'"
            (click)="resendInvite()"
          >
            Uitnodiging opnieuw versturen
          </ui-button>
        }
        @if(this.user?.two_factor_enabled) {
          <ui-button
            [variant]="'secondary'"
            (click)="reset2FA()"
          >
            Reset 2FA
          </ui-button>
        }
      </div>
    </form>
  </ui-modal>`,
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsersModalComponent {
  userService = inject(UserService);
  notificationService = inject(NotificationService);
  confirmService = inject(ConfirmService);

  private modalRef = inject(MODAL_REF);
  modalService = inject(ModalService);

  user: User | null = null;
  form: FormGroup = new FormGroup({});

  ngOnInit() {
    this.form = new FormGroup({
      name: new FormControl(this.user?.name, Validators.required),
      email: new FormControl(this.user?.email, [Validators.required, Validators.email]),
      is_admin: new FormControl(this.user?.is_admin || false),
      is_consulent: new FormControl(this.user?.is_consulent || false),
      is_key_user: new FormControl(this.user?.is_key_user || false),
    });
  }

  closeModal() {
    this.modalService.close(this.modalRef as ModalRef<any>);
  }

  saveModal() {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.notificationService.error('Er zijn fouten in het formulier');
      return;
    }

    if (this.user?.id) {
      this.userService.updateUser(this.user.id, this.form.value).subscribe({
        next: (user) => {
          this.notificationService.success('Gebruiker succesvol aangepast');
          this.modalService.close(this.modalRef as ModalRef<any>);
        },
        error: (error) => {
          const errorMessage = error.error.message ?? 'Er is iets fout gegaan bij het aanpassen van de gebruiker';
          this.notificationService.error(errorMessage);
        }
      });
    } else {
      this.userService.createUser(this.form.value).subscribe({
        next: (user) => {
          this.notificationService.success('Gebruiker succesvol aangemaakt');
          this.modalService.close(this.modalRef as ModalRef<any>);
        },
        error: (error) => {
          const errorMessage = error.error.message ?? 'Er is iets fout gegaan bij het aanmaken van de gebruiker';
          this.notificationService.error(errorMessage);
        }
      });
    }
  }

  resendInvite() {
    if (!this.user?.id) {
      return;
    }

    this.userService.resendInvite(this.user.id).subscribe({
      next: () => {
        this.notificationService.success('Uitnodiging succesvol verstuurd');
      },
      error: (error) => {
        this.notificationService.error('Er is iets fout gegaan bij het versturen van de uitnodiging');
      }
    });
  }

  reset2FA() {
    if (!this.user?.id) {
      return;
    }

    this.confirmService.confirm({
      title: '2FA reset',
      message: 'Weet u zeker dat u de twee-factor authenticatie voor deze gebruiker wilt resetten?',
      confirmText: 'Ja, reset 2FA',
      cancelText: 'Annuleren'
    }).then((confirmed) => {
      if (confirmed) {
        this.userService.reset2FA(this.user!.id).subscribe({
          next: () => {
            this.notificationService.success('2FA succesvol gereset');
            this.user!.two_factor_enabled = false;
          },
          error: (error) => {
            this.notificationService.error('Er is iets fout gegaan bij het resetten van 2FA');
          }
        });
      }
    });
  }
}
